import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxmUserauthV1RealmAccessQueryServiceRealmAccessGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-realm-access-query-service-realm-access-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1RealmAccessState } from '@interxfi/types';
import { RealmAccessBreadcrumb } from '../../../../../../features/userauth/realm-access/userauth-realm-access-breadcrumb';
import { UserauthRealmAccessEntityMenu } from '../../../../../../features/userauth/realm-access/userauth-realm-access-entity-menu';
import { UserauthRealmAccessEventSummary } from '../../../../../../features/userauth/realm-access/userauth-realm-access-event';
import { getUserAuthRealmAccessStateListItems } from '../../../../../../features/userauth/realm-access/userauth-realm-access-list-items';
import { getUserauthRealmAccessDetailPath } from '../../../../../../features/userauth/realm-access/userauth-realm-access-routing';
import { UserauthRealmAccessStatusPill } from '../../../../../../features/userauth/realm-access/userauth-realm-access-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/realm-access/_realm-access/$realmAccessId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `realm-access-${params.realmAccessId}`,
        label: <RealmAccessBreadcrumb realmAccessId={params.realmAccessId} />,
        to: getUserauthRealmAccessDetailPath(params.realmAccessId),
        suffix: (
          <CopyButton
            content={params.realmAccessId}
            label="Copy Realm Access ID"
            tooltip={{
              copy: params.realmAccessId,
              copied: 'Copied Realm Access ID',
            }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Realm Access'),
  component: RealmAccessDetail,
});

function getListItems(data: IxmUserauthV1RealmAccessState | undefined) {
  const items = getUserAuthRealmAccessStateListItems(data);

  return {
    left: [items.organizationName, items.realmName, items.tenantName, items.data.spec.isAdmin] as const satisfies ReadonlyListItem[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.realmAccessId,
      items.realmId,
      items.tenantId,
      items.organizationId,
    ] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load realm access', 'view this realm access');

function RealmAccessDetail() {
  const { realmAccessId } = Route.useParams();
  const request = { realmAccessId } as const;
  const { data, error, isPending } = useIxmUserauthV1RealmAccessQueryServiceRealmAccessGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.realmAccess), [data?.realmAccess]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <UserauthRealmAccessStatusPill isPending={showPending} size="large" status={data?.realmAccess?.status} />
          </HorizontalStack>

          <UserauthRealmAccessEntityMenu
            isAdmin={data?.realmAccess.data?.spec?.isAdmin}
            organizationId={data?.realmAccess.organizationId}
            realmAccessId={data?.realmAccess.realmAccessId ?? realmAccessId}
            realmId={data?.realmAccess.realmId}
            tenantId={data?.realmAccess.tenantId}
          />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <UserauthRealmAccessEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
